import React, { useEffect } from "react";
import Slider from "react-slick";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingCart, User } from "lucide-react";
import WhatsAppButton from "./WhatsAppButton";
import Footer from "./Footer";
import Header from "./Header";
import { addDoc, collection } from "firebase/firestore";
import { db } from "../firebase";
import AddToCartButton from "./AddToCart";
import { getUserId } from "../utils/utils";

const ProductPage = () => {
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
  };

  const productImages = [
    "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcShCqMk8g638gCFHEGOTrUBEOjrnYTJIQiVhg&s",
    "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcShCqMk8g638gCFHEGOTrUBEOjrnYTJIQiVhg&s",
    "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRni62bkp1qxNUN0b51XLCXoyaz6z9MIeiuPQ&s",
  ];

  const navigate = useNavigate();
  const location = useLocation();
  const productData = location.state?.product;
  const userId = getUserId();

  // Mock product details - replace with actual data
  const productDetails = {
    title: "Silver Chain Necklace",
    shortDescription:
      "Handcrafted Sterling Silver Chain with Traditional Design",
    longDescription:
      "This exquisite piece combines traditional craftsmanship with contemporary elegance. Each link is carefully crafted from pure sterling silver, ensuring both durability and luxury. Perfect for both everyday wear and special occasions.",
    highlights: [
      "Made from 925 Sterling Silver",
      "Traditional handcrafted design",
      "Suitable for daily wear",
      "Tarnish-resistant finish",
    ],
    specifications: [
      ["Gross Weight", "15.930 g"],
      ["Metal Weight", "14.260 g"],
      ["Purity", "22 KT"],
      ["Length", "18 inch"],
      ["Gender", "Women"],
    ],
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [productData]);
  const addToCart = async (product) => {
    try {
      const cartRef = collection(db, `users/${userId}/cart`);
      await addDoc(cartRef, { ...product, quantity: 1 });
    } catch (error) {
      console.error("Error adding to cart:", error);
      throw error;
    }
  };
  return (
    <div className="min-h-screen flex flex-col bg-purple-50">
      <Header />

      {/* Main Content */}
      <main className="flex-grow py-12 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            {/* Product Image Slider */}
            <div className="md:col-span-7">
              <div className="bg-purple-50 rounded-2xl p-8">
                <Slider {...sliderSettings}>
                  {productData?.images.map((image, index) => (
                    <div key={index} className="aspect-square">
                      <Zoom>
                        <img
                          src={image}
                          alt={`Product Image ${index + 1}`}
                          className="w-full h-full object-contain rounded-lg"
                        />
                      </Zoom>
                    </div>
                  ))}
                </Slider>
              </div>
            </div>

            {/* Product Info */}
            <div className="md:col-span-5">
              <div className="space-y-8">
                {/* Title and Description */}
                <div className="border-b border-purple-100 pb-6">
                  <h1 className="text-4xl font-serif text-purple-900 mb-3">
                    {productData?.name || productDetails.title}
                  </h1>
                  <p className="text-lg font-medium text-purple-700 mb-4">
                    {productDetails.shortDescription}
                  </p>
                  <p className="text-purple-600 leading-relaxed">
                    {productDetails.longDescription}
                  </p>
                </div>

                {/* Product Highlights */}
                <div className="bg-purple-50 rounded-xl p-6">
                  <h3 className="text-lg font-medium text-purple-900 mb-4">
                    Product Highlights
                  </h3>
                  <ul className="space-y-2">
                    {productDetails.highlights.map((highlight, index) => (
                      <li
                        key={index}
                        className="flex items-center text-purple-600"
                      >
                        <span className="w-2 h-2 bg-purple-400 rounded-full mr-3"></span>
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Price */}
                <div className="space-y-2">
                  <p className="text-3xl font-serif text-purple-900">
                    ₹{productData?.price || "1,17,338"}
                  </p>
                  <p className="text-sm text-purple-500">
                    Save ₹7,130 (Original Price: ₹1,24,468)
                  </p>
                </div>

                {/* Options */}
                <div className="bg-purple-50 p-6 rounded-xl space-y-4">
                  <h3 className="text-lg font-medium text-purple-900">
                    Available Options
                  </h3>
                  <select className="w-full p-3 border border-purple-200 rounded-lg bg-white text-purple-700 focus:ring-2 focus:ring-purple-200">
                    <option value="14.260g">
                      {productData?.specifications?.grossWeight} g - ₹
                      {productData?.price}
                    </option>
                  </select>
                </div>

                {/* Add to Cart Button */}
                <AddToCartButton
                  onClick={(e) => {
                    addToCart(productData);
                  }}
                />

                {/* Help Section */}
                <div className="border-t border-purple-100 pt-8">
                  <h4 className="text-lg font-medium text-purple-900 mb-2">
                    Need Help?
                  </h4>
                  <p className="text-purple-600">
                    Call or WhatsApp us at:{" "}
                    <a
                      href="tel:+917022981870"
                      className="text-purple-900 font-medium hover:underline"
                    >
                      +91 7022981870
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Product Details Section */}
          <section className="py-16 border-t border-purple-100 mt-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <h3 className="text-2xl font-serif text-purple-900 mb-8">
                  Product Details
                </h3>
                <table className="w-full">
                  <tbody className="divide-y divide-purple-100">
                    {productData?.specifications &&
                      Object.entries(productData.specifications).map(
                        ([key, value]) => (
                          <tr key={key}>
                            <td className="py-4 text-purple-600 capitalize">
                              {key}
                            </td>
                            <td className="py-4 text-purple-900 font-medium">
                              {value}
                            </td>
                          </tr>
                        )
                      )}
                  </tbody>
                </table>
              </div>

              <div>
                <h3 className="text-2xl font-serif text-purple-900 mb-8">
                  Certifications
                </h3>
                <div className="flex gap-8">
                  <img
                    src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR8T1CCCThJtOlp8a39O_kf3POvk7o8jH8csw&s"
                    alt="BIS Certified"
                    className="h-20"
                  />
                  <img
                    src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ5Cq9ieWECJNfUz-VBzhfVq563Z42I5q5yXA&s"
                    alt="IGI Certified"
                    className="h-20"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Related Products */}
          <section className="py-16 border-t border-purple-100">
            <h3 className="text-2xl font-serif text-purple-900 mb-8">
              You May Also Like
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                "Silver Necklace",
                "Gold Earrings",
                "Diamond Ring",
                "Pearl Bracelet",
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-purple-100"
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSbHC_6-tFeCA22vd9eNGPmo_xyFtCw51qOyg&s"
                      alt={item}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="text-lg font-medium text-purple-900">
                      {item}
                    </h4>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>

      <WhatsAppButton />
      <Footer />
    </div>
    // <div className="min-h-screen flex flex-col bg-gray-50">
    //   {/* Header */}
    //   <Header/>

    //   {/* Main Content */}
    //   <main className="flex-grow py-12 bg-white">
    //     <div className="container mx-auto px-6">
    //       <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
    //         {/* Product Image Slider */}
    //         <div className="md:col-span-7">
    //           <div className="bg-gray-50 rounded-2xl p-8">
    //             <Slider {...sliderSettings}>
    //               {[productData?.image, ...productImages].map((image, index) => (
    //                 <div key={index} className="aspect-square">
    //                   <Zoom>
    //                     <img
    //                       src={image}
    //                       alt={`Product Image ${index + 1}`}
    //                       className="w-full h-full object-contain rounded-lg"
    //                     />
    //                   </Zoom>
    //                 </div>
    //               ))}
    //             </Slider>
    //           </div>
    //         </div>

    //         {/* Product Info */}
    //         <div className="md:col-span-5">
    //           <div className="space-y-8">
    //             {/* Title and Description */}
    //             <div className="border-b pb-6">
    //               <h1 className="text-4xl font-serif text-gray-900 mb-3">
    //                 {productData?.name || productDetails.title}
    //               </h1>
    //               <p className="text-lg font-medium text-gray-600 mb-4">
    //                 {productDetails.shortDescription}
    //               </p>
    //               <p className="text-gray-600 leading-relaxed">
    //                 {productDetails.longDescription}
    //               </p>
    //             </div>

    //             {/* Product Highlights */}
    //             <div className="bg-gray-50 rounded-xl p-6">
    //               <h3 className="text-lg font-medium text-gray-900 mb-4">
    //                 Product Highlights
    //               </h3>
    //               <ul className="space-y-2">
    //                 {productDetails.highlights.map((highlight, index) => (
    //                   <li key={index} className="flex items-center text-gray-600">
    //                     <span className="w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
    //                     {highlight}
    //                   </li>
    //                 ))}
    //               </ul>
    //             </div>

    //             {/* Price */}
    //             <div className="space-y-2">
    //               <p className="text-3xl font-serif text-gray-900">
    //                 ₹{productData?.price || "1,17,338"}
    //               </p>
    //               <p className="text-sm text-gray-500">
    //                 Save ₹7,130 (Original Price: ₹1,24,468)
    //               </p>
    //             </div>

    //             {/* Options */}
    //             <div className="bg-gray-50 p-6 rounded-xl space-y-4">
    //               <h3 className="text-lg font-medium text-gray-900">
    //                 Available Options
    //               </h3>
    //               <select className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-700 focus:ring-2 focus:ring-gray-400">
    //                 <option value="14.260g">14.260 g - ₹1,17,338</option>
    //               </select>
    //             </div>

    //             {/* Add to Cart Button */}
    //             <button className="w-full py-4 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors duration-300 font-medium">
    //               Add to Cart
    //             </button>

    //             {/* Help Section */}
    //             <div className="border-t pt-8">
    //               <h4 className="text-lg font-medium text-gray-900 mb-2">
    //                 Need Help?
    //               </h4>
    //               <p className="text-gray-600">
    //                 Call or WhatsApp us at:{" "}
    //                 <a
    //                   href="tel:+917022981870"
    //                   className="text-gray-900 font-medium hover:underline"
    //                 >
    //                   +91 7022981870
    //                 </a>
    //               </p>
    //             </div>
    //           </div>
    //         </div>
    //       </div>

    //       {/* Product Details Section */}
    //       <section className="py-16 border-t mt-16">
    //         <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
    //           <div>
    //             <h3 className="text-2xl font-serif text-gray-900 mb-8">
    //               Product Details
    //             </h3>
    //             <table className="w-full">
    //               <tbody className="divide-y">
    //                 {productDetails.specifications.map(([key, value]) => (
    //                   <tr key={key}>
    //                     <td className="py-4 text-gray-600">{key}</td>
    //                     <td className="py-4 text-gray-900 font-medium">{value}</td>
    //                   </tr>
    //                 ))}
    //               </tbody>
    //             </table>
    //           </div>

    //           <div>
    //             <h3 className="text-2xl font-serif text-gray-900 mb-8">
    //               Certifications
    //             </h3>
    // <div className="flex gap-8">
    //   <img
    //     src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR8T1CCCThJtOlp8a39O_kf3POvk7o8jH8csw&s"
    //     alt="BIS Certified"
    //     className="h-20"
    //   />
    //   <img
    //     src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ5Cq9ieWECJNfUz-VBzhfVq563Z42I5q5yXA&s"
    //     alt="IGI Certified"
    //     className="h-20"
    //   />
    // </div>
    //           </div>
    //         </div>
    //       </section>

    //       {/* Related Products */}
    //       <section className="py-16 border-t">
    //         <h3 className="text-2xl font-serif text-gray-900 mb-8">
    //           You May Also Like
    //         </h3>
    //         <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
    //           {["Silver Necklace", "Gold Earrings", "Diamond Ring", "Pearl Bracelet"].map((item, idx) => (
    //             <div
    //               key={idx}
    //               className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
    //             >
    //               <div className="aspect-square overflow-hidden">
    //                 <img
    //                   src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSbHC_6-tFeCA22vd9eNGPmo_xyFtCw51qOyg&s"
    //                   alt={item}
    //                   className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
    //                 />
    //               </div>
    //               <div className="p-4">
    //                 <h4 className="text-lg font-medium text-gray-900">{item}</h4>
    //               </div>
    //             </div>
    //           ))}
    //         </div>
    //       </section>
    //     </div>
    //   </main>

    //   <WhatsAppButton />

    //   {/* Footer */}
    //   <Footer/>
    // </div>
  );
};

export default ProductPage;
